export const CATALOG_ATTACHMENT_MAX_COUNT = 30;
export const CATALOG_IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';
export const CATALOG_FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.rtf,.zip,application/pdf';

export type CatalogAttachmentKind = 'image' | 'file';

export interface CatalogAttachment {
  id: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  url: string;
  kind: CatalogAttachmentKind;
  isPrimary: boolean;
  sortOrder: number;
  createdAt?: string;
}

const kinds: CatalogAttachmentKind[] = ['image', 'file'];

const optionalText = (value: unknown): string | undefined => {
  const text = String(value ?? '').trim();
  return text || undefined;
};

const optionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeKind = (value: unknown): CatalogAttachmentKind => {
  const normalized = String(value ?? '').trim();
  const camel = normalized.charAt(0).toLowerCase() + normalized.slice(1);
  return kinds.includes(camel as CatalogAttachmentKind) ? camel as CatalogAttachmentKind : 'file';
};

export const normalizeCatalogAttachment = (value: any): CatalogAttachment => ({
  id: String(value?.id ?? ''),
  fileName: String(value?.fileName ?? ''),
  contentType: String(value?.contentType ?? ''),
  fileSize: optionalNumber(value?.fileSize) ?? 0,
  url: String(value?.url ?? ''),
  kind: normalizeKind(value?.kind),
  isPrimary: Boolean(value?.isPrimary),
  sortOrder: optionalNumber(value?.sortOrder) ?? 0,
  createdAt: optionalText(value?.createdAt),
});

export const normalizeCatalogAttachments = (value: unknown): CatalogAttachment[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map(normalizeCatalogAttachment)
    .filter(item => item.id)
    .sort((left, right) => {
      if (left.isPrimary !== right.isPrimary) return left.isPrimary ? -1 : 1;
      if (left.sortOrder !== right.sortOrder) return left.sortOrder - right.sortOrder;
      return (left.createdAt || '').localeCompare(right.createdAt || '');
    });
};

export const splitCatalogAttachments = (attachments: CatalogAttachment[]) => ({
  images: attachments.filter(item => item.kind === 'image'),
  files: attachments.filter(item => item.kind !== 'image'),
});

export const formatCatalogFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[unitIndex]}`;
};

export type CatalogFileTypeId =
  | 'pdf'
  | 'word'
  | 'excel'
  | 'csv'
  | 'powerpoint'
  | 'text'
  | 'rtf'
  | 'zip'
  | 'file';

export type CatalogFileTypeTone = 'pdf' | 'doc' | 'sheet' | 'slide' | 'archive' | 'text' | 'file';

export interface CatalogFileTypeToken {
  id: CatalogFileTypeId;
  tone: CatalogFileTypeTone;
  badge: string;
  labelKey: `fileType${'Pdf' | 'Word' | 'Excel' | 'Csv' | 'PowerPoint' | 'Text' | 'Rtf' | 'Zip' | 'File'}`;
}

const extensionOf = (fileName: string): string => {
  const trimmed = fileName.trim();
  const lastDot = trimmed.lastIndexOf('.');
  if (lastDot <= 0 || lastDot >= trimmed.length - 1) return '';
  return trimmed.slice(lastDot + 1).toLowerCase();
};

const badgeFrom = (extension: string, fallback: string): string =>
  (extension || fallback).toUpperCase().slice(0, 4);

/** Maps a catalog file to a visible type badge (PDF, Word, Excel, …). */
export const resolveCatalogFileType = (fileName: string, contentType = ''): CatalogFileTypeToken => {
  const extension = extensionOf(fileName);
  const mime = contentType.trim().toLowerCase();

  if (extension === 'pdf' || mime === 'application/pdf' || mime.endsWith('/pdf')) {
    return { id: 'pdf', tone: 'pdf', badge: 'PDF', labelKey: 'fileTypePdf' };
  }

  if (extension === 'csv' || mime === 'text/csv' || mime === 'application/csv') {
    return { id: 'csv', tone: 'sheet', badge: 'CSV', labelKey: 'fileTypeCsv' };
  }

  if (
    extension === 'xls'
    || extension === 'xlsx'
    || mime.includes('spreadsheet')
    || mime === 'application/vnd.ms-excel'
  ) {
    return { id: 'excel', tone: 'sheet', badge: badgeFrom(extension, 'XLS'), labelKey: 'fileTypeExcel' };
  }

  if (
    extension === 'doc'
    || extension === 'docx'
    || mime.includes('wordprocessingml')
    || mime === 'application/msword'
  ) {
    return { id: 'word', tone: 'doc', badge: badgeFrom(extension, 'DOC'), labelKey: 'fileTypeWord' };
  }

  if (
    extension === 'ppt'
    || extension === 'pptx'
    || mime.includes('presentation')
    || mime === 'application/vnd.ms-powerpoint'
  ) {
    return { id: 'powerpoint', tone: 'slide', badge: badgeFrom(extension, 'PPT'), labelKey: 'fileTypePowerPoint' };
  }

  if (extension === 'zip' || mime.includes('zip') || mime.includes('compressed')) {
    return { id: 'zip', tone: 'archive', badge: badgeFrom(extension, 'ZIP'), labelKey: 'fileTypeZip' };
  }

  if (extension === 'rtf' || mime === 'application/rtf' || mime === 'text/rtf') {
    return { id: 'rtf', tone: 'text', badge: 'RTF', labelKey: 'fileTypeRtf' };
  }

  if (extension === 'txt' || mime === 'text/plain' || mime.startsWith('text/')) {
    return { id: 'text', tone: 'text', badge: badgeFrom(extension, 'TXT'), labelKey: 'fileTypeText' };
  }

  if (extension) {
    return { id: 'file', tone: 'file', badge: badgeFrom(extension, 'FILE'), labelKey: 'fileTypeFile' };
  }

  return { id: 'file', tone: 'file', badge: 'FILE', labelKey: 'fileTypeFile' };
};
